import {Challenge, ClassStep} from "models"
import React, {useMemo} from "react"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component"
import AdminLayout from "components/admin/layout"
import AppHistory from "shared/history"
import Button from "components/inputs/button"
import Checkbox from "components/inputs/checkbox"
import Checkboxes from "components/inputs/checkboxes"
import {digg} from "diggerize"
import {FlashNotifications as FlashMessage} from "flash-notifications"
import inflection from "inflection"
import Input from "components/inputs/input"
import Locales from "shared/locales"
import memo from "set-state-compare/build/memo"
import Routes from "shared/routes"
import setLayout from "shared/set-layout"
import {SurveysSelectWithContainer} from "components/surveys/select"
import useI18n from "i18n-on-steroids/src/use-i18n.mjs"
import useModel from "@kaspernj/api-maker/build/use-model"
import {v4 as uuidv4} from "uuid"

const selectedChallengeAttributes = ["id", "name", "public", "surveyId"]

for (const locale of Locales.availableLocales()) {
  selectedChallengeAttributes.push(`name${locale.substring(0, 1).toUpperCase()}${locale.substring(1, 99)}`)
}

export default memo(shapeComponent(class RoutesAdminChallengesEdit extends ShapeComponent {
  setup() {
    setLayout("admin")

    const {t} = useI18n({namespace: "js.routes.admin.challenges.edit"})
    const {challenge, challengeId} = useModel(Challenge, {
      match: this.p.match,
      newIfNoId: {
        defaults: () => ({id: uuidv4()})
      },
      preload: ["challenge_class_steps"],
      select: {
        Challenge: selectedChallengeAttributes,
        ChallengeClassStep: ["class_step_id"]
      }
    })

    this.setInstance({challenge, challengeId, t})
    this.useStates({classStepsCollection: undefined})

    useMemo(() => {
      this.loadClassSteps()
    }, [])
  }

  async loadClassSteps() {
    const classSteps = await ClassStep
      .ransack({s: "position"})
      .select({ClassStep: ["id", "name"]})
      .toArray()

    this.setState({
      classStepsCollection: classSteps.map((classStep) => [classStep.name(), classStep.id()])
    })
  }


  render() {
    const {challenge} = this.tt

    return (
      <AdminLayout active="challenges" className="routes--admin--challenges--edit" headerTitle={this.headerTitle()}>
        {challenge &&
          <form onSubmit={this.tt.onSubmit}>
            {Locales.availableLocales().map((locale) =>
              <React.Fragment key={locale}>
                <Input
                  attribute={`name${inflection.camelize(locale)}`}
                  label={`${Challenge.humanAttributeName("name")} (${Locales.labelForLocale(locale)})`}
                  model={challenge}
                />
              </React.Fragment>
            )}
            {this.s.classStepsCollection &&
              <Checkboxes
                defaultValue={
                  challenge.isPersisted() ? challenge.challengeClassSteps().map((challengeClassStep) => challengeClassStep.classStepId()) : null
                }
                label={ClassStep.modelName().human({count: 2})}
                name="challenge[class_step_ids]"
                options={this.s.classStepsCollection}
              />
            }
            <SurveysSelectWithContainer attribute="surveyId" label={Challenge.humanAttributeName("survey")} model={challenge} />
            <Checkbox attribute="public" model={challenge} />
            <Button primary save />
          </form>
        }
      </AdminLayout>
    )
  }

  headerTitle() {
    const {challenge} = this.tt

    if (challenge?.isNewRecord()) {
      return this.t(".add_new_challenge")
    } else if (challenge?.isPersisted()) {
      return this.t(".edit_challenge", {name: challenge.name()})
    }
  }

  onSubmit = async (e) => {
    e.preventDefault()

    const form = digg(e, "target")
    const formData = new FormData(form)
    const {challenge} = this.tt

    if (challenge.isNewRecord()) formData.append("challenge[id]", challenge.id())

    try {
      await challenge.saveRaw(formData, {form})
      FlashMessage.success(this.t(".the_challenge_was_saved"))
      AppHistory.push(Routes.adminChallengePath(challenge.id()))
    } catch (error) {
      FlashMessage.errorResponse(error)
    }
  }
}))
